import { createRouter } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import {
  TABLE_CONTAINER_SELECTOR,
  TABLE_SCROLL_TO_TOP_SELECTOR,
} from '@/shared/lib/use-table-scroll-reset';
import { routeTree } from './routeTree.gen';

/**
 * Create the typed router. The query client is passed as router context so
 * route loaders can call `ensureQueryData` to prefetch server state.
 */
export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    // Load route data only after navigation. Intent preloading triggers route
    // loaders on sidebar hover, which causes page requests before the page is
    // opened.
    defaultPreload: false,
    // Query owns server-state caching; the router should not also cache loaders.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    // Paginated tables reset to the top on every navigation (page changes
    // included): without these, restoration would write the saved scrolled
    // position back after render. Detail pages keep their restored scroll —
    // only table scrollers match.
    scrollToTopSelectors: [TABLE_CONTAINER_SELECTOR, TABLE_SCROLL_TO_TOP_SELECTOR],
  });
}

// Register the router type for end-to-end inference across the app.
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
